import { useTranslation } from "@redotech/redo-i18n";
import BoldIcon from "@redotech/redo-icons/bold-01.svg";
import ItalicIcon from "@redotech/redo-icons/italic-01_filled.svg";
import StrikethroughIcon from "@redotech/redo-icons/strikethrough-01_filled.svg";
import UnderlineIcon from "@redotech/redo-icons/underline-01_filled.svg";
import { AlignToolbarButton } from "@redotech/redo-web/components/plate-ui/align-toolbar-button";
import { ClearFormattingToolbarButton } from "@redotech/redo-web/components/plate-ui/clear-formatting-toolbar-button";
import { ColorToolbarButton } from "@redotech/redo-web/components/plate-ui/color-toolbar-button";
import { EmojiToolbarButton } from "@redotech/redo-web/components/plate-ui/emoji-toolbar-button";
import { FontFamilyToolbarButton } from "@redotech/redo-web/components/plate-ui/font-family-toolbar-button";
import { FontSizeToolbarButton } from "@redotech/redo-web/components/plate-ui/font-size-toolbar-button";
import { LinkToolbarButton } from "@redotech/redo-web/components/plate-ui/link-toolbar-button";
import { ListToolbarButton } from "@redotech/redo-web/components/plate-ui/list-toolbar-button";
import { MarkToolbarButton } from "@redotech/redo-web/components/plate-ui/mark-toolbar-button";
import { ImageToolbarButton } from "@redotech/redo-web/components/plate-ui/media-toolbar-buttons";
import { ToolbarGroup } from "@redotech/redo-web/components/plate-ui/toolbar";
import { ToolbarWithOverflow } from "@redotech/redo-web/components/plate-ui/toolbar-overflow";
import { KEYS } from "platejs";
import { memo } from "react";

export interface MacroEditorToolbarProps {
  /**
   * Embeds the picked image inline in the macro body. Mirrors the composer's
   * image button; omit to hide it (e.g. while the editor is read-only).
   */
  onImageSelect?: (file: File) => void;
}

/**
 * Formatting toolbar for the macro message editor. Mirrors the composer's
 * EmailToolbar but drops the AI / Macros / file buttons, which don't apply when
 * authoring a macro (non-image attachments have their own section). The image
 * button is kept so agents can add inline body images without drag-and-drop.
 */
export const MacroEditorToolbar = memo(function MacroEditorToolbar({
  onImageSelect,
}: MacroEditorToolbarProps) {
  const { t } = useTranslation();
  return (
    <ToolbarWithOverflow className="border-border px-1 py-1">
      <FontFamilyToolbarButton />
      <FontSizeToolbarButton />

      <ToolbarGroup>
        <MarkToolbarButton
          Icon={BoldIcon}
          nodeType={KEYS.bold}
          tooltip={t("Bold (⌘+B)")}
        />
        <MarkToolbarButton
          Icon={ItalicIcon}
          nodeType={KEYS.italic}
          tooltip={t("Italic (⌘+I)")}
        />
        <MarkToolbarButton
          Icon={UnderlineIcon}
          nodeType={KEYS.underline}
          tooltip={t("Underline (⌘+U)")}
        />
        <MarkToolbarButton
          Icon={StrikethroughIcon}
          nodeType={KEYS.strikethrough}
          tooltip={t("Strikethrough")}
        />
      </ToolbarGroup>

      <ColorToolbarButton />
      <AlignToolbarButton />
      <ListToolbarButton />
      <LinkToolbarButton />
      <EmojiToolbarButton />
      {onImageSelect && <ImageToolbarButton onImageSelect={onImageSelect} />}
      <ClearFormattingToolbarButton />
    </ToolbarWithOverflow>
  );
});
